import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Info, Music, Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { createMixEnhancePreview, downloadEnhancerTrack, buildFullMixEnhanceBody, validateFullMixEnhanceBody, resolveEnhanceDownloadUrl } from "../../../../redux/actions/audioEngineer";
import DropdownMenu from "../../../../components/DropdownMenu";
import { GenreAndStyleDropdown } from "./GenreAndStyleDropdown";
import { EnhancementOptions } from "./EnhancementOptions";
import { Tooltip } from "../../../../components/Tooltip";
import ProcessingLoader from "./ProcessingLoader";
import { MixEnhancerPackModal } from "./MixEnhancerPackModal";
import { useLocation, useNavigate } from "react-router-dom";

export default function PreviewSection({ demoMode }) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const audioRef = useRef(null);
    const svgRef = useRef(null);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const pageTab = params.get("tab");
    const pageType = params.get("type");
    const [isProcessing, setIsProcessing] = useState(false);
    const [mixRequestData, setMixRequestData] = useState(null)
    const [showMixEnhancerModal, setShowMixEnhancerModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false)
    const [selectedOption, setSelectedOption] = useState('');
    const [isTrackMastered, setIsTrackMastered] = useState(false)
    const [masterMix, setMasterMix] = useState(false);
    const [processStems, setProcessStems] = useState(false);
    const [showMaster, setShowMaster] = useState(false)
    const [showMasteredPreview, setShowMastaredPreview] = useState(false)

    // Playback state
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0); // from audio when NOT dragging
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState(null);
    const [isAudioLoading, setIsAudioLoading] = useState(false);
    const [isPlayInitiated, setIsPlayInitiated] = useState(false); // Track when play is clicked but hasn't started yet

    // Dragging state
    const [isDragging, setIsDragging] = useState(false);
    const [dragProgress, setDragProgress] = useState(null); // temp progress while dragging
    const [wasPlayingBeforeDrag, setWasPlayingBeforeDrag] = useState(false);

    const audioEngineerReducer = useSelector((state) => state.audioEngineer);
    const { uploadedData, mixPreview, mixMetaData } = useSelector((state) => state.audioEngineer);
    // console.log('mixPreview', mixPreview, 'mixMetaData', mixMetaData, 'uploadedData', uploadedData)

    const handleSelectedOption = (option) => {
        setSelectedOption(option)
    }

    let previewArray = [];
    if (Array.isArray(audioEngineerReducer?.preview)) {
        previewArray = audioEngineerReducer.preview;
    } else if (Array.isArray(audioEngineerReducer?.preview?.data)) {
        previewArray = audioEngineerReducer.preview.data;
    } else if (audioEngineerReducer?.preview && typeof audioEngineerReducer.preview === 'object') {
        // treat single preview object as an array with one item
        previewArray = [audioEngineerReducer.preview];
    } else {
        previewArray = [];
    }
    const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);
    const selectedTrack = Array.isArray(previewArray) && previewArray.length > 0 ? previewArray[selectedPreviewIndex] : null;
    const previewUrlFromArray = previewArray.length > 0
        ? (
            previewArray[selectedPreviewIndex]?.previewUrl
            || previewArray[selectedPreviewIndex]?.download_url_mastered_preview
            || previewArray[selectedPreviewIndex]?.previewMasterTaskResults?.download_url_mastered_preview
        )
        : null;
    const previewUrl = previewUrlFromArray || audioEngineerReducer?.previewStatus?.previewUrl || null;

    const [IsLoudness, setIsLoudness] = useState(null);
    const [enhancements, setEnhancements] = useState({
        fixClipping: true,
        fixLoudness: true,
        fixStereoWidth: true,
        fixDynamicRange: true,
        fixTonalBalance: true
    });

    // SVG geometry
    const VIEWBOX = 220;
    const CX = VIEWBOX / 2; // 110
    const CY = VIEWBOX / 2; // 110
    const RADIUS = 90;

    // Avoid timeupdate overwriting right after a programmatic seek
    const lastSeekRef = useRef(0);
    const IGNORE_AFTER_SEEK_MS = 250; // ignore timeupdate for 250ms after seek
    const handledPlayRef = useRef(false);

    // Determine the actual audio source being used
    const actualAudioSource = mixPreview?.isFinalMixed
        ? mixPreview?.mixedUrl
        : showMaster
            ? mixPreview?.mixedPreviewUrl
            : uploadedData?.data?.[0]?.readable_url;

    // --- Audio event wiring ---
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onLoadedMeta = () => {
            let d = audio.duration;

            if ((!d || !isFinite(d)) && audio.seekable?.length > 0) {
                d = audio.seekable.end(audio.seekable.length - 1);
            }

            if (d && isFinite(d)) {
                setDuration(d);
            }

            setIsAudioLoading(false);
        };

        const onCanPlay = () => {
            setIsAudioLoading(false);
        };

        const onCanPlayThrough = () => {
            setIsAudioLoading(false);
        };

        const onLoadedData = () => {
            setIsAudioLoading(false);
        };

        const onLoadStart = () => {
            setIsAudioLoading(true);
        };

        const onPlay = () => {
            setIsPlayInitiated(false);
        };

        const onTimeUpdate = () => {
            const now = Date.now();
            if (now - lastSeekRef.current < IGNORE_AFTER_SEEK_MS) return;

            const t = audio.currentTime || 0;
            const d = audio.duration;

            setCurrentTime(t);

            if (!isDragging) {
                if (d && !isNaN(d) && isFinite(d)) {
                    setProgress((t / d) * 100);
                } else if (audio.seekable && audio.seekable.length > 0) {
                    const end = audio.seekable.end(audio.seekable.length - 1);
                    if (end > 0) {
                        setProgress((t / end) * 100);
                    }
                }
            }
        };

        const onEnded = () => {
            setIsPlaying(false);
            setProgress(0);
            setIsPlayInitiated(false);
        };

        const onError = (e) => {
            const audio = audioRef.current;

            console.error("Audio error", {
                src: audio?.currentSrc,
                networkState: audio?.networkState,
                readyState: audio?.readyState,
                error: audio?.error
            });

            setError("Failed to load audio");
            setIsPlaying(false);
        };

        audio.addEventListener("loadedmetadata", onLoadedMeta);
        audio.addEventListener("canplay", onCanPlay);
        audio.removeEventListener("canplaythrough", onCanPlayThrough);
        audio.removeEventListener("loadeddata", onLoadedData);
        audio.addEventListener("loadstart", onLoadStart);
        audio.addEventListener("play", onPlay);
        audio.addEventListener("timeupdate", onTimeUpdate);
        audio.addEventListener("ended", onEnded);
        audio.addEventListener("error", onError);

        return () => {
            audio.removeEventListener("loadedmetadata", onLoadedMeta);
            audio.removeEventListener("canplay", onCanPlay);
            audio.removeEventListener("canplaythrough", onCanPlayThrough);
            audio.removeEventListener("loadeddata", onLoadedData);
            audio.removeEventListener("loadstart", onLoadStart);
            audio.removeEventListener("play", onPlay);
            audio.removeEventListener("timeupdate", onTimeUpdate);
            audio.removeEventListener("ended", onEnded);
            audio.removeEventListener("error", onError);
        };
    }, [isDragging, actualAudioSource]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !actualAudioSource) return;

        setError(null);
        setProgress(0);
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);

        try {
            audio.src = actualAudioSource;
            audio.load();

            const playNow = !!audioEngineerReducer?.preview?.playNow;

            if (playNow && !handledPlayRef.current) {
                audio.play()
                    .then(() => setIsPlaying(true))
                    .catch(() => setIsPlaying(false));

                handledPlayRef.current = true;
            }
        } catch (e) {
            console.warn('Failed to set audio.src', e);
        }
    }, [actualAudioSource]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const interval = setInterval(() => {
            // fallback when duration is not available (signed/streamed URLs)
            if ((!audio.duration || !isFinite(audio.duration)) && audio.seekable?.length > 0) {
                const end = audio.seekable.end(audio.seekable.length - 1);
                if (end > 0) {
                    setDuration(end);
                }
            }
        }, 500);

        return () => clearInterval(interval);
    }, [actualAudioSource]);

    useEffect(() => {
        handledPlayRef.current = false;
    }, [audioEngineerReducer?.preview]);

    useEffect(() => {
        if (!Array.isArray(previewArray) || previewArray.length === 0) {
            setSelectedPreviewIndex(0);
            return;
        }
        if (selectedPreviewIndex >= previewArray.length) setSelectedPreviewIndex(0);
    }, [previewArray]);

    useEffect(() => {
        if (Array.isArray(previewArray) && previewArray.length > 0) {
            setSelectedPreviewIndex(0);
        }
    }, [audioEngineerReducer?.preview]);

    const enhanceDownloadUrl = resolveEnhanceDownloadUrl(mixPreview);
    const canDownloadEnhanced = showMasteredPreview && (
        mixPreview?.isFinalMixed ||
        mixPreview?.isMixEnhanced ||
        Boolean(enhanceDownloadUrl)
    );

    const getDownloadItems = () => [
        {
            label: "Download as WAV file",
            icon: <Music size={16} />,
            onClick: () => dispatch(downloadEnhancerTrack("wav", setIsDownloading)),
        },
        {
            label: "Download as MP3 file",
            icon: <Music size={16} />,
            onClick: () => dispatch(downloadEnhancerTrack('mp3', setIsDownloading)),
        },
        {
            label: "Download as M4A file",
            icon: <Music size={16} />,
            onClick: () => dispatch(downloadEnhancerTrack('m4a', setIsDownloading)),
        },
        {
            label: "Download as AAC file",
            icon: <Music size={16} />,
            onClick: () => dispatch(downloadEnhancerTrack('aac', setIsDownloading)),
        },
        {
            label: "Download as OGG file",
            icon: <Music size={16} />,
            onClick: () => dispatch(downloadEnhancerTrack('ogg', setIsDownloading)),
        },
        {
            label: "Download as AIFF file",
            icon: <Music size={16} />,
            onClick: () => dispatch(downloadEnhancerTrack('aiff', setIsDownloading)),
        }
    ];

    // Play / Pause
    const togglePlay = async () => {
        const audio = audioRef.current;
        if (!audio) return;
        try {
            if (audio.paused) {
                setIsPlayInitiated(true);
                await audio.play();
                setIsPlaying(true);
            } else {
                audio.pause();
                setIsPlaying(false);
            }
        } catch (err) {
            console.error("Playback error", err);
            setIsPlayInitiated(false);
            // setError("Playback failed");
        }
    };

    const clientToProgress = (clientX, clientY) => {
        const svg = svgRef.current;
        if (!svg) return null;
        const rect = svg.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = clientX - cx;
        const dy = clientY - cy;

        let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
        angleDeg += 90;
        if (angleDeg < 0) angleDeg += 360;
        return (angleDeg / 360) * 100;
    };

    const startDrag = (clientX, clientY) => {
        const audio = audioRef.current;
        const initial = clientToProgress(clientX, clientY) ?? 0;

        setWasPlayingBeforeDrag(Boolean(audio && !audio.paused && !audio.ended));
        try {
            if (audio && !audio.paused) audio.pause();
        } catch (e) {
        }

        setDragProgress(initial);
        setIsDragging(true);

        window.addEventListener("mousemove", onPointerMove);
        window.addEventListener("mouseup", onPointerUp);
        window.addEventListener("touchmove", onPointerMove, { passive: false });
        window.addEventListener("touchend", onPointerUp);

        document.body.style.userSelect = "none";
    };

    const onPointerDown = (e) => {
        e.preventDefault();
        const isTouch = e.type === "touchstart";
        const clientX = isTouch ? e.touches[0].clientX : e.clientX;
        const clientY = isTouch ? e.touches[0].clientY : e.clientY;
        startDrag(clientX, clientY);
    };

    const onPointerMove = (e) => {
        if (!isDragging) return;
        if (e.cancelable) e.preventDefault();
        const isTouch = e.type === "touchmove";
        const clientX = isTouch ? e.touches[0].clientX : e.clientX;
        const clientY = isTouch ? e.touches[0].clientY : e.clientY;

        const p = clientToProgress(clientX, clientY);
        if (p != null) setDragProgress(p);
    };

    const onPointerUp = (e) => {
        if (!isDragging) return;
        if (e.cancelable) e.preventDefault();

        let final = dragProgress;
        if (final == null) {
            const isTouch = e.type === "touchend";
            if (!isTouch && e.clientX != null) {
                final = clientToProgress(e.clientX, e.clientY) ?? final;
            }
        }
        if (final == null) final = 0;

        const audio = audioRef.current;
        if (audio && audio.duration && !isNaN(audio.duration)) {
            const newTime = (final / 100) * audio.duration;
            lastSeekRef.current = Date.now();
            try {
                audio.currentTime = newTime;
            } catch (err) {
                console.warn("Seeking failed:", err);
            }

            setProgress(final);
            setCurrentTime(newTime);
        }

        if (audio && wasPlayingBeforeDrag) {
            audio
                .play()
                .then(() => setIsPlaying(true))
                .catch(() => setIsPlaying(false));
        } else {
            setIsPlaying(Boolean(audio && !audio.paused));
        }

        setIsDragging(false);
        setDragProgress(null);
        setWasPlayingBeforeDrag(false);

        // cleanup listeners
        window.removeEventListener("mousemove", onPointerMove);
        window.removeEventListener("mouseup", onPointerUp);
        window.removeEventListener("touchmove", onPointerMove);
        window.removeEventListener("touchend", onPointerUp);

        document.body.style.userSelect = "";
    };

    const activeProgress = dragProgress != null ? dragProgress : progress || 0;

    const pointerAngle = (activeProgress / 100) * 360 - 90;
    const pointerRad = (pointerAngle * Math.PI) / 180;
    const pointerX = CX + RADIUS * Math.cos(pointerRad);
    const pointerY = CY + RADIUS * Math.sin(pointerRad);

    useEffect(() => {
        return () => {
            window.removeEventListener("mousemove", onPointerMove);
            window.removeEventListener("mouseup", onPointerUp);
            window.removeEventListener("touchmove", onPointerMove);
            window.removeEventListener("touchend", onPointerUp);
            document.body.style.userSelect = "";
        };
    }, []);

    const validStyles = ['ROCK_INDIE', 'POP', 'ACOUSTIC', 'HIPHOP_GRIME', 'ELECTRONIC', 'REGGAE_DUB', 'ORCHESTRAL', 'METAL', 'OTHER', 'GRITTY_CRUNCHY', 'BRIGHT', 'WARM', 'SHARP_BASSY', 'THUMPING_BOOMY', 'MELLOW_SMOOTH', 'AIRY_EXPANSIVE', 'AGGRESSIVE', 'BALANCED'];

    const buildEnhanceOptions = useCallback(() => {
        const upperSelectedOption = selectedOption?.toUpperCase();
        const musicalStyle = validStyles.includes(upperSelectedOption) ? upperSelectedOption : 'OTHER';

        return {
            musicalStyle,
            isMaster: isTrackMastered,
            fixClippingIssues: enhancements.fixClipping,
            fixDRCIssues: enhancements.fixDynamicRange,
            fixStereoWidthIssues: enhancements.fixStereoWidth,
            fixTonalProfileIssues: enhancements.fixTonalBalance,
            fixLoudnessIssues: enhancements.fixLoudness,
            applyMastering: masterMix,
            loudnessPreference: IsLoudness || 'NO_CHANGE',
            stemProcessing: processStems,
        };
    }, [selectedOption, isTrackMastered, enhancements, masterMix, IsLoudness, processStems]);

    const getEnhanceRequestData = useCallback(() => {
        return buildFullMixEnhanceBody(audioEngineerReducer, buildEnhanceOptions());
    }, [audioEngineerReducer, buildEnhanceOptions]);

    const validateEnhanceRequest = useCallback((body) => {
        return validateFullMixEnhanceBody(body, mixMetaData, audioEngineerReducer?.mixPreview);
    }, [mixMetaData, audioEngineerReducer?.mixPreview]);

    const handleGetPreview = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setProgress(0);
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);

        const reqBody = getEnhanceRequestData();
        dispatch(createMixEnhancePreview(reqBody, setIsProcessing, setShowMaster))
        navigate('/admin/urstudio/mix-enhancer?tab=settings')
        // setShowMaster(true)
    }

    const handleGetEnhancedMix = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setProgress(0);
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);

        const reqBody = getEnhanceRequestData();
        const validationError = validateEnhanceRequest(reqBody);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        setMixRequestData(reqBody);
        setShowMixEnhancerModal(true)
    }

    useEffect(() => {
        if (pageType === "preview") {
            setShowMaster(true)
            setShowMastaredPreview(false)
        }
        if (pageTab === "final") {
            setShowMaster(false)
            setShowMastaredPreview(true)
        }
    }, [pageTab])

    const onPaymentSuccess = useCallback(() => {
        setShowMaster(false)
        setShowMastaredPreview(true)
        navigate('/admin/urstudio/mix-enhancer?tab=settings')
    }, [navigate])

    return (
        <div className="bg-white flex flex-col items-center gap-4 relative mx-auto rounded-2xl p-4 lg:p-5">
            {/* Audio element */}
            <audio
                key={actualAudioSource}
                ref={audioRef}
                src={actualAudioSource}
                preload="metadata"
                className="hidden"
                onError={(e) => {
                    console.error("Audio load error", e);
                    setError("Failed to load audio");
                }}
            />
            {isProcessing
                ? <ProcessingLoader isMaster={showMaster} uploadId={mixPreview?._id || null} taskId={mixPreview?.roex_task_id || null}/>
                : <>
                    <h2 className="text-theme_clr4 text-base font-semibold text-center">{demoMode ? "TRACK.mp3" : mixPreview ? mixPreview?.projectName : uploadedData?.data?.[0]?.filename}</h2>

                    {/* Circular Player */}
                    <div className="relative w-[200px] h-[200px]">
                        <svg ref={svgRef} className="w-full h-full" viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} aria-hidden>
                            {/* static orange ring */}
                            <circle cx={CX} cy={CY} r={RADIUS} stroke="#FA6138" strokeWidth="6" fill="none" />

                            {/* subtle background track */}
                            <circle cx={CX} cy={CY} r={RADIUS} stroke="rgba(26,26,58,0.08)" strokeWidth="2" fill="none" />

                            {/* draggable pointer */}
                            <circle
                                cx={pointerX}
                                cy={pointerY}
                                r={9}
                                fill="#FA6138"
                                stroke="#fff"
                                strokeWidth="2"
                                style={{ cursor: isDragging ? "grabbing" : "grab" }}
                                onMouseDown={onPointerDown}
                                onTouchStart={onPointerDown}
                                filter="url(#glow)"
                            />

                            {/* glow definition */}
                            <defs>
                                <filter id="glow">
                                    <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                                    <feMerge>
                                        <feMergeNode in="coloredBlur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                            </defs>

                            {/* center visual */}
                            <g transform={`translate(${CX - 36}, ${CY - 36})`}>
                                <rect rx={36} ry={36} width={72} height={72} fill="#00067e" />
                            </g>
                        </svg>

                        {/* Center Play/Pause button */}
                        <button
                            onClick={togglePlay}
                            aria-label={isPlaying ? "Pause" : "Play"}
                            className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[130px] h-[130px] rounded-full flex items-center justify-center text-white"
                            style={{ backgroundColor: "#00067e" }}
                        >
                            {isAudioLoading || isPlayInitiated ? (
                                <div className="relative w-full h-full flex items-center justify-center">
                                    <div className="w-12 h-12 rounded-full border-4 border-[#FA6138] border-t-transparent animate-spin"></div>
                                </div>
                            ) : isPlaying ? (
                                <Pause className="w-8 h-8" />
                            ) : (
                                <Play className="w-8 h-8" />
                            )}
                        </button>
                    </div>

                    {/* Audio element */}
                    {/* <audio
                        key={actualAudioSource}
                        ref={audioRef}
                        src={actualAudioSource}
                        preload="auto"
                        // crossOrigin="anonymous"
                        onError={(e) => {
                            console.error("Audio load error", e);
                            setError("Failed to load audio");
                        }}
                    /> */}

                    {showMasteredPreview &&
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <span className="text-theme_clr2 text-sm">You can find your enhanced {masterMix ? 'master' : 'mix'} in your {masterMix ? 'Masters' : 'Mixes'} library</span>
                            <Tooltip
                                text={`Visit your ${masterMix ? 'Masters' : 'Mixes'} Library below to access or manage your enhanced versions${masterMix ? '.' : ' and stem splits.'}`}
                                style={{ bg: "bg-white", text: "text-theme_clr4", width: "w-48" }}
                                position="right"
                            >
                                <Info className="w-4 h-4 text-theme_clr2" />
                            </Tooltip>
                        </div>
                    }

                    {!showMasteredPreview &&
                        <>
                            <div className="w-[70%] mx-auto" data-tour="genre-selection">
                                <GenreAndStyleDropdown
                                    backgroundColor={'bg-theme_clr4'}
                                    getSelectedOption={handleSelectedOption}
                                    defaultValue={selectedOption}
                                />
                            </div>
                            <div data-tour="is-mastered">
                                <div className="flex items-center justify-center gap-2 mt-2 mb-2">
                                    <span className="text-theme_clr4 text-sm">Is your track mastered?</span>
                                    <Tooltip
                                        text="A mastered track is professionally polished, optimized for loudness, and ready for release."
                                        style={{ bg: "bg-white", text: "text-theme_clr4", width: "w-48" }}
                                        position="right"
                                    >
                                        <Info className="w-4 h-4 text-theme_clr4" />
                                    </Tooltip>
                                </div>
                                <div className="flex gap-3 justify-center">
                                    <button
                                        onClick={() => setIsTrackMastered(true)}
                                        className={`px-8 py-1 rounded-full font-normal transition ${isTrackMastered
                                            ? 'bg-theme_clr1 text-theme_clr4'
                                            : 'bg-white text-theme_clr4 border border-theme_clr1'
                                            }`}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        onClick={() => setIsTrackMastered(false)}
                                        className={`px-8 py-1 rounded-full font-normal transition ${!isTrackMastered
                                            ? 'bg-theme_clr1 text-theme_clr4'
                                            : 'bg-white text-theme_clr4 border border-theme_clr1'
                                            }`}
                                    >
                                        No
                                    </button>
                                </div>
                            </div>
                            {/*Enhancement Options */}
                            <EnhancementOptions
                                enhancements={enhancements}
                                onEnhancementsChange={setEnhancements}
                            />

                            {/* Mastering Section */}
                            <div className="px-6 pb-4" data-tour="mastering-option">
                                <div className="flex items-center justify-center gap-2 mb-3">
                                    <span className="text-theme_clr4 font-semibold text-lg">Mastering</span>
                                    <Tooltip
                                        text="Mastering"
                                        style={{ bg: "bg-white", text: "text-theme_clr4", width: "w-48" }}
                                        position="right"
                                    >
                                        <Info className="w-4 h-4 text-theme_clr4" />
                                    </Tooltip>
                                </div>

                                <div className="space-y-4">
                                    {/* Master Mix and Loudness in same row */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {/* Master Mix */}
                                        <div>
                                            <div className="flex items-center justify-center gap-2 mb-2">
                                                <span className="text-theme_clr4 text-sm">Would you like to master your enhanced mix?</span>
                                                <Tooltip
                                                    text="Mastering applies final polish—loudness, EQ, and dynamics—for release-ready quality."
                                                    style={{ bg: "bg-white", text: "text-theme_clr4", width: "w-48" }}
                                                    position="right"
                                                >
                                                    <Info className="w-4 h-4 text-theme_clr4" />
                                                </Tooltip>
                                            </div>
                                            <div className="flex gap-3 justify-center">
                                                <button
                                                    onClick={() => setMasterMix(true)}
                                                    className={`px-8 py-1 rounded-full font-normal transition ${masterMix
                                                        ? 'bg-theme_clr1 text-theme_clr4'
                                                        : 'bg-white text-theme_clr4 border border-theme_clr1'
                                                        }`}
                                                >
                                                    Yes
                                                </button>
                                                <button
                                                    onClick={() => setMasterMix(false)}
                                                    className={`px-8 py-1 rounded-full font-normal transition ${!masterMix
                                                        ? 'bg-theme_clr1 text-theme_clr4'
                                                        : 'bg-white text-theme_clr4 border border-theme_clr1'
                                                        }`}
                                                >
                                                    No
                                                </button>
                                            </div>
                                        </div>

                                        {/* Loudness */}
                                        <div>
                                            <div className="flex items-center justify-center gap-2 mb-2">
                                                <span className="text-theme_clr4 text-sm">Loudness</span>
                                                <Tooltip
                                                    title="Loudness (CD / Streaming)"
                                                    text={`
                                                <ol style="margin:0;padding-left:1rem;line-height:1.35">
<li><strong>CD:</strong> “Target loudness for physical releases like CDs (typically louder).”</li>
<li><strong>Streaming:</strong> “Optimize loudness for digital streaming platforms like Spotify or Apple Music.”</li>
</ol>
                                            `}
                                                    style={{ bg: "bg-white", text: "text-theme_clr4", width: "w-48" }}
                                                    position="right"
                                                >
                                                    <Info className="w-4 h-4 text-theme_clr4" />
                                                </Tooltip>
                                            </div>
                                            <div className="flex gap-3 justify-center">
                                                <button
                                                    onClick={() => setIsLoudness('CD_LOUDNESS')}
                                                    className={`px-8 py-1 rounded-full font-normal transition ${IsLoudness === 'CD_LOUDNESS'
                                                        ? 'bg-theme_clr1 text-theme_clr4'
                                                        : 'bg-white text-theme_clr4 border border-theme_clr1'
                                                        }`}
                                                >
                                                    CD
                                                </button>
                                                <button
                                                    onClick={() => setIsLoudness('STREAMING_LOUDNESS')}
                                                    className={`px-8 py-1 rounded-full font-normal transition ${IsLoudness === 'STREAMING_LOUDNESS'
                                                        ? 'bg-theme_clr1 text-theme_clr4'
                                                        : 'bg-white text-theme_clr4 border border-theme_clr1'
                                                        }`}
                                                >
                                                    Streaming
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Stems */}
                            <div data-tour="stems">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <span className="text-theme_clr4 font-semibold">Stems</span>
                                    <Tooltip
                                        text="Stem processing"
                                        style={{ bg: "bg-white", text: "text-theme_clr4", width: "w-48" }}
                                        position="right"
                                    >
                                        <Info className="w-4 h-4 text-theme_clr4" />
                                    </Tooltip>
                                </div>
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <span className="text-theme_clr4 text-sm">Would you like to individually process your stems?</span>
                                    <Tooltip
                                        text="Enable stem processing if you want the AI to enhance each instrument group separately before recombining."
                                        style={{ bg: "bg-white", text: "text-theme_clr4", width: "w-48" }}
                                        position="right"
                                    >
                                        <Info className="w-4 h-4 text-theme_clr4" />
                                    </Tooltip>
                                </div>
                                <div className="flex gap-3 justify-center">
                                    <button
                                        onClick={() => setProcessStems(true)}
                                        className={`px-8 py-1 rounded-full font-normal transition ${processStems
                                            ? 'bg-theme_clr1 text-theme_clr4'
                                            : 'bg-white text-theme_clr4 border border-theme_clr1'
                                            }`}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        onClick={() => setProcessStems(false)}
                                        className={`px-8 py-1 rounded-full font-normal transition ${!processStems
                                            ? 'bg-theme_clr1 text-theme_clr4'
                                            : 'bg-white text-theme_clr4 border border-theme_clr1'
                                            }`}
                                    >
                                        No
                                    </button>
                                </div>
                            </div>
                        </>
                    }
                    {showMaster ? (
                        <button onClick={handleGetEnhancedMix} disabled={isProcessing} className="mt-6 w-full max-w-md bg-theme_clr4 text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium">
                            {isProcessing ? 'Processing your track...' : `Get Your Enhanced ${masterMix ? 'Master' : 'Mix'}`}
                        </button>
                    ) : canDownloadEnhanced ? (
                        <DropdownMenu
                            button={
                                <button disabled={isDownloading} className="mt-6 w-[50vw] max-w-md bg-theme_clr2 text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium flex items-center justify-center gap-2">
                                    {isDownloading ? (
                                        <div className="relative w-6 h-6 flex items-center justify-center">
                                            <div className="w-6 h-6 rounded-full border-2 border-[#FFFFFF] border-t-transparent animate-spin"></div>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="flex items-center justify-center h-6 w-6">
                                                <Download size={16} />
                                            </span>
                                            Download Your Enhanced {masterMix ? 'Master' : 'Mix'}
                                        </>
                                    )}
                                </button>
                            }
                            fullWidth={true}
                            items={getDownloadItems()}
                        />
                    ) : (
                        <button onClick={handleGetPreview} data-tour="preview-button" disabled={isProcessing} className="mt-6 w-full max-w-md bg-theme_clr4 text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium">
                            {isProcessing ? 'Processing your track...' : 'Get your enhanced preview'}
                        </button>
                    )}

                    {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
                    <MixEnhancerPackModal
                        isOpen={showMixEnhancerModal}
                        onClose={() => setShowMixEnhancerModal(false)}
                        onPaymentSuccess={onPaymentSuccess}
                        requestData={mixRequestData}
                        getEnhanceRequestData={getEnhanceRequestData}
                        validateEnhanceRequest={validateEnhanceRequest}
                        setIsProcessing={setIsProcessing}
                    />
                </>
            }
        </div>
    );
}

